import { config } from '@ps/config/client';
import type {
  BasicUserProfileInfo,
  CommentMessage,
  CommentResponse,
} from './types';

const base = () => config.awsPopclipServiceBaseUrl;

export async function addCommentAPI(params: {
  clip_id: string;
  text_content: string;
  user_id: string;
  user_meta?: BasicUserProfileInfo;
}): Promise<CommentMessage> {
  const response = await fetch(`${base()}/api/popclips/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error('Failed to add comment');
  }

  const json = (await response.json()) as { data: CommentResponse };
  const comment = json.data;

  return {
    id: comment.comment_id,
    userId: comment.user_id,
    username: comment.user_meta?.username,
    text: comment.comment_text,
    name: comment.user_meta?.fullName || 'Unknown User',
    avatar: comment.user_meta?.avatar || '',
    replyCount: 0,
    status: 'added',
    type: 'comment',
    createdAt: comment.created_at,
    updatedAt: comment.updated_at,
    timestamp: comment.timestamp,
  };
}

export async function fetchComments(params: {
  clip_id: string;
  pageSize?: number;
  lastEvaluatedKey: {
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null;
}): Promise<{
  totalCount: number;
  comments: CommentMessage[];
  lastEvaluatedKey: {
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null;
}> {
  const response = await fetch(`${base()}/api/popclips/comments/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch comments');
  }

  const json = (await response.json()) as {
    data: {
      totalCount: number;
      comments: CommentResponse[];
      lastEvaluatedKey: {
        clip_id: string;
        comment_id: string;
        customer_id: string;
      } | null;
    };
  };

  const commentResponse = json.data.comments || [];

  const formattedComments: CommentMessage[] = commentResponse.map(
    (comment) => ({
      id: comment.comment_id,
      userId: comment.user_id,
      username: comment.user_meta?.username,
      text: comment.comment_text,
      name: comment.user_meta?.fullName || 'Unknown User',
      avatar: comment.user_meta?.avatar || '',
      replyCount: comment.reply_count ?? 0,
      isDeleted: comment.is_deleted,
      status: 'added' as const,
      type: 'comment' as const,
      createdAt: comment.created_at,
      updatedAt: comment.updated_at,
      timestamp: comment.timestamp,
    }),
  );

  return {
    comments: formattedComments,
    lastEvaluatedKey: json.data.lastEvaluatedKey,
    totalCount: json.data.totalCount,
  };
}

export async function getCommentsCount(params: {
  clip_id: string;
}): Promise<{ commentsCount: number }> {
  const response = await fetch(`${base()}/api/popclips/comments/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch comments count');
  }

  const json = (await response.json()) as {
    data: { totalCount: number };
  };

  return { commentsCount: json.data.totalCount || 0 };
}

export async function deleteCommentAPI(
  clipId: string,
  timestamp: number,
  userId: string,
): Promise<void> {
  const response = await fetch(`${base()}/api/popclips/comments`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      clip_id: clipId,
      timestamp,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to delete comment');
  }
}

export async function createPopclipViewCount(
  clipId: string,
  timestamp: number,
): Promise<void> {
  try {
    await fetch(`${base()}/api/popclips/views`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'psl_popclips',
        clip_id: clipId,
        clip_timestamp: timestamp.toString(),
      }),
    });
  } catch {
    // View count is non-critical, silently fail
  }
}
